import { useState } from "react";
import { Link } from "react-router-dom";
import { Accessibility, HeartHandshake } from "lucide-react";
import "./login-shell.css";

const COPY = {
  control: {
    label: "Control OS · Internes Team",
    title: "Schön, dass du für andere da bist.",
    subtitle: "Gemeinsam sorgen wir für ein gutes Miteinander.",
    eyebrow: "Das Menschliche bleibt im Mittelpunkt",
    story: ["Für Menschen.", "Mit Verantwortung."],
    description: "Du hilfst, Begegnungen möglich zu machen. Aufmerksam, respektvoll und mit Herz.",
    help: "Auch für dich sind wir da.", helpText: "Unterstützung für deinen Arbeitsalltag.",
    helpHref: "/passwort-vergessen",
  },
  member: {
    label: "Mitglieder-App · Dein Miteinander",
    title: "Schön, dass du da bist.",
    subtitle: "Dein Tempo. Deine Interessen. Deine Begegnungen.",
    eyebrow: "Willkommen bei Visibilis Life",
    story: ["Begegnungen,", "die guttun."],
    description: "Menschen kennenlernen. Gemeinsam lachen. Und einfach du selbst sein.",
    help: "Eine Frage? Wir sind für dich da.", helpText: "Hilfe beim Zugang, wenn du sie brauchst.",
    helpHref: "/passwort-vergessen",
  },
  organization: {
    label: "Organisations-Cockpit",
    title: "Gemeinsam machen wir mehr möglich.",
    subtitle: "Dein Engagement bringt Menschen zusammen.",
    eyebrow: "Raum für Miteinander",
    story: ["Dein Engagement.", "Echte Begegnungen."],
    description: "Menschen verbinden. Gemeinsam etwas bewegen. Mit Angeboten, die von Herzen kommen.",
    help: "Wir begleiten dich und dein Team.", helpText: "Unterstützung für euren persönlichen Zugang.",
    helpHref: "/zugang/passwort-vergessen",
  },
};

// Display choices only. Credentials and authentication remain with each app.
function DisplayOptions({ choices, setChoices }) {
  return (
    <details className="vl-login__display">
      <summary><Accessibility size={20} aria-hidden="true" /><span>Ansicht anpassen</span></summary>
      <fieldset>
        <legend>Deine Ansicht</legend>
        {[["contrast", "Hoher Kontrast"], ["large", "Grosse Schrift"]].map(([key, label]) => (
          <label key={key}><input type="checkbox" checked={choices[key]} onChange={(e) => setChoices((old) => ({ ...old, [key]: e.target.checked }))} />{label}</label>
        ))}
        <p>Diese Einstellung gilt für diese Anmeldeseite. Es gibt keine dekorativen Animationen.</p>
      </fieldset>
    </details>
  );
}

export default function LoginShell({ variant = "control", title, subtitle, children, testId, headerExtras, accessibility, notice, afterForm }) {
  const copy = COPY[variant];
  const [choices, setChoices] = useState({ contrast: false, large: false });
  return (
    <div className="vl-login" data-variant={variant} data-contrast={choices.contrast} data-large={choices.large}>
      <a className="vl-login__skip" href="#visibilis-login-main">Zum Anmeldeformular springen</a>
      <aside className="vl-login__story" aria-label="Willkommen bei Visibilis Life">
        <img className="vl-login__photo" src="/brand/login-heart.webp" alt="Drei Menschen unterhalten sich an einem Cafétisch; einer sitzt im Rollstuhl." fetchPriority="high" />
        <div className="vl-login__shade" aria-hidden="true" />
        <img className="vl-login__logo" src="/brand/visibilis-logo-3d.png" alt="VISIBILIS.LIFE – Menschen mit Behinderungen" width="1524" height="1032" />
        <div className="vl-login__story-copy">
          <p className="vl-login__eyebrow">{copy.eyebrow}</p>
          <p className="vl-login__story-title">{copy.story.map((line) => <span key={line}>{line}</span>)}</p>
          <p className="vl-login__description">{copy.description}</p>
        </div>
      </aside>
      <div className="vl-login__content">
        <header className="vl-login__header">
          {headerExtras}
          <span lang="de" className="vl-login__language">Deutsch</span>
          {accessibility || <DisplayOptions choices={choices} setChoices={setChoices} />}
        </header>
        <main id="visibilis-login-main" tabIndex={-1} className="vl-login__main" data-testid={testId}>
          <p className="vl-login__eyebrow vl-login__role">{copy.label}</p>
          <h1>{title || copy.title}</h1>
          <p className="vl-login__subtitle">{subtitle === undefined ? copy.subtitle : subtitle}</p>
          {notice}
          <div className="vl-login__form">{children}</div>
          {afterForm}
          <section className="vl-login__help" aria-labelledby="visibilis-login-help">
            <HeartHandshake size={36} aria-hidden="true" />
            <div><h2 id="visibilis-login-help">{copy.help}</h2><p>{copy.helpText}</p><Link to={copy.helpHref}>Hilfe beim Anmelden</Link></div>
          </section>
        </main>
        <footer className="vl-login__footer"><a href="https://visibilis.life/" rel="noreferrer">Zur VISIBILIS-Website</a><Link to={copy.helpHref}>Hilfe beim Zugang</Link></footer>
      </div>
    </div>
  );
}
